import React, { useEffect, useState, useCallback } from 'react';
import { PiUsersDuotone, PiTrashDuotone, PiCrownDuotone, PiShieldCheckDuotone } from 'react-icons/pi';
import {
  changeRoomMemberRole,
  getRoomMembers,
  removeRoomMember,
} from '../../../api/roomService';
import { useAuth } from '../../../app/providers/AuthContext';
import { publicUserName, safeUsername } from '../../utils/userDisplay';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Badge from '../../common/Badge';
import Select from '../../common/Select';

const OWNER_ROLE = 'OWNER';
const ADMIN_ROLE = 'ADMIN';
const MEMBER_ROLE = 'MEMBER';
const MEMBERS_LOAD_ERROR = 'Не удалось загрузить участников';
const MEMBER_REMOVE_ERROR = 'Не удалось удалить участника';
const ROLE_CHANGE_ERROR = 'Не удалось изменить роль участника';
const ROLE_SELECT_PLACEHOLDER = 'Роль';
const ROLE_SAVING_LABEL = 'Сохраняем...';
const ROOM_MEMBER_REMOVE_LABEL = 'Удалить участника';

const ROLE_LABELS = {
  [OWNER_ROLE]: 'Владелец',
  [ADMIN_ROLE]: 'Админ',
  [MEMBER_ROLE]: 'Участник',
};

const ROLE_OPTIONS = [
  { value: ADMIN_ROLE, label: ROLE_LABELS[ADMIN_ROLE], hint: 'Может управлять участниками' },
  { value: MEMBER_ROLE, label: ROLE_LABELS[MEMBER_ROLE], hint: 'Обычный участник комнаты' },
];

const ROLE_ICONS = {
  [OWNER_ROLE]: <PiCrownDuotone size={14} />,
  [ADMIN_ROLE]: <PiShieldCheckDuotone size={14} />,
};

const getCurrentUserId = (user) => user?.id ?? user?.userId;
const isSameUser = (leftUserId, rightUserId) => (
  leftUserId != null && rightUserId != null && String(leftUserId) === String(rightUserId)
);
/**
 * Модал со списком участников комнаты.
 * OWNER может менять роли ADMIN/MEMBER, OWNER/ADMIN могут удалять обычных участников.
 */
export default function RoomMembersModal({ isOpen, onClose, roomId }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [changingRoleId, setChangingRoleId] = useState(null);

  const currentUserId = getCurrentUserId(user);
  const currentMember = members.find((member) => isSameUser(member.userId, currentUserId));
  const canManageMembers = currentMember?.role === OWNER_ROLE || currentMember?.role === ADMIN_ROLE;
  const canManageRoles = currentMember?.role === OWNER_ROLE;

  const loadMembers = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    setError('');
    try {
      const membersData = await getRoomMembers(roomId);
      setMembers(Array.isArray(membersData) ? membersData : []);
    } catch {
      setError(MEMBERS_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (isOpen) loadMembers();
  }, [isOpen, loadMembers]);

  const handleRemove = async (userId) => {
    setRemovingId(userId);
    setError('');
    try {
      await removeRoomMember(roomId, userId);
      setMembers((prev) => prev.filter((member) => !isSameUser(member.userId, userId)));
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || MEMBER_REMOVE_ERROR);
    } finally {
      setRemovingId(null);
    }
  };

  const handleRoleChange = async (member, role) => {
    if (!member || member.role === role) return;

    setChangingRoleId(member.userId);
    setError('');
    try {
      const updatedMember = await changeRoomMemberRole(roomId, member.userId, role);
      setMembers((prev) => prev.map((item) => (
        isSameUser(item.userId, member.userId)
          ? { ...item, ...updatedMember, role: updatedMember?.role || role }
          : item
      )));
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(msg || ROLE_CHANGE_ERROR);
    } finally {
      setChangingRoleId(null);
    }
  };

  const roleBadgeStatus = (role) => {
    if (role === OWNER_ROLE) return 'primary';
    if (role === ADMIN_ROLE) return 'open';
    return 'closed';
  };

  const canEditRole = (member) => canManageRoles && member.role !== OWNER_ROLE;
  const canReserveRemoveSlot = (member) => canManageMembers && canEditRole(member);
  const canRemoveMember = (member) => canManageMembers && member.role === MEMBER_ROLE;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Участники комнаты" size="md">
      {error && <div className="bm-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <p className="bm-text-secondary bm-text-sm">Загрузка...</p>
      ) : members.length === 0 ? (
        <p className="bm-text-secondary bm-text-sm" style={{ textAlign: 'center', padding: '16px 0' }}>
          Нет участников
        </p>
      ) : (
        <div className="bm-room-members-list">
          {members.map((member) => (
            <div key={member.userId} className="bm-room-member-card">
              <PiUsersDuotone size={18} className="bm-room-member-card__icon" />

              <div className="bm-room-member-card__content">
                <div className="bm-room-member-card__identity">
                  <span className="bm-room-member-card__name bm-text-sm">
                    {publicUserName(member)}
                  </span>
                  {safeUsername(member) && (
                    <span className="bm-room-member-card__username bm-text-secondary bm-text-sm">
                      @{safeUsername(member)}
                    </span>
                  )}
                </div>
                <div className="bm-room-member-card__joined bm-text-secondary">
                  С {new Date(member.joinedAt).toLocaleDateString()}
                </div>

              </div>

              <div className="bm-room-member-card__actions">
                {canEditRole(member) ? (
                  <div className="bm-room-member-card__role-control">
                    <Select
                      id={`room-member-${member.userId}-role`}
                      value={member.role}
                      onChange={(role) => handleRoleChange(member, role)}
                      options={ROLE_OPTIONS}
                      placeholder={ROLE_SELECT_PLACEHOLDER}
                      disabled={changingRoleId === member.userId}
                    />
                    {changingRoleId === member.userId && (
                      <span className="bm-room-member-card__saving">{ROLE_SAVING_LABEL}</span>
                    )}
                  </div>
                ) : (
                  <Badge status={roleBadgeStatus(member.role)} className="bm-room-member-card__role">
                    {ROLE_ICONS[member.role] && (
                      <span className="bm-room-member-card__role-icon">{ROLE_ICONS[member.role]}</span>
                    )}
                    {ROLE_LABELS[member.role] || member.role}
                  </Badge>
                )}

                {canRemoveMember(member) ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<PiTrashDuotone size={14} />}
                    onClick={() => handleRemove(member.userId)}
                    loading={removingId === member.userId}
                    disabled={changingRoleId === member.userId}
                    className="bm-room-member-card__remove"
                    title={ROOM_MEMBER_REMOVE_LABEL}
                    aria-label={ROOM_MEMBER_REMOVE_LABEL}
                  />
                ) : canReserveRemoveSlot(member) ? (
                  <span className="bm-room-member-card__remove-spacer" aria-hidden="true" />
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
